import FloorFilter from './lib/FlatFloorFilter/FlatFloorFilter';
import FlatAvailableFilter from './lib/FlatAvailabileFilter/FlatAvailableFilter';
import FlatAreaFilter from './lib/FlatAreaFilter/FlatAreaFilter';
import FlatRoomFilter from './lib/FlatRoomFilter/FlatRoomFilter';

import { useState } from 'react';
import { useSVGContext } from '../../../../../../contexts/GraphicContext';
import { useFlatsDataContext } from '../../../../../../contexts/FlatsDataContext';

import styles from './FlatsFilters.module.css';
import { paths } from '../../../../../../settings';

const floors = [
    { value: 'parter', label: 'Parter' },
    { value: 'pietro_1', label: '1' },
    { value: 'pietro_2', label: '2' },
    { value: 'pietro_3', label: '3' }
];

const FlatsFilters: React.FC<{}> = (): JSX.Element => {
    const [ showResetButton, setShowResetButton] = useState(false);
    const [ resetFilters, setResetFilters] = useState<boolean>(false);
    const [ selectedFloor, setSelectedFloor ] = useState<string | null>(null);
    const { handleContextFloorClick } = useSVGContext();
    const { setSecondarySingleFilter } = useFlatsDataContext();

    const [filters, setFilters] = useState({
        floor: '',
        availability: [],
        rooms: [],
        areaFrom: null,
        areaTo: null
    });

    /**
     * Sets a specific filter key to the provided value, updating the filters state.
     * @param {string} key - The key of the filter to set ('floor', 'rooms', 'areaFrom', 'areaTo').
     * @param {any} value - The value to set for the specified filter key.
     * @returns {void}
    */
    const setFilter = (key: string, value: any): void => {
        setFilters(prevFilters => ({
            ...prevFilters,
            [key]: value,
        }));
    };

    /**
     * Handles selecting a Floor filter by updating the filter and setting the selected Floor.
     * @param {string} floor - The Floor to set.
     * @returns {void}
    */
    const handleFloorSelect = (floor: string): void => {
        setFilter('floor', floor);
        setSelectedFloor(floor);
    };

    /**
     * Handles selecting the number of rooms filter by updating the filters state.
     * @param {number[]} rooms - Array of room numbers to set as the selected rooms filter.
     * @returns {void}
    */
    const handleRoomSelect = (rooms: number[]): void => {
        setFilter('rooms', [...rooms]);
    };

    /**
     * Handles changing the area filter by setting 'areaFrom' or 'areaTo' with the specified value.
     * @param {'areaFrom' | 'areaTo'} key - The key for the area filter to update.
     * @param {number | null} value - The value to set.
     * @returns {void}
    */
    const handleAreaFilterChange = (key: 'areaFrom' | 'areaTo', value: number | null): void => setFilter(key, value);

    /**
     * Submits the current filters and applies them to the Context.
     * Controlling the reset button visibility.
     * If there are active filters, the reset button is shown. Selected Floor and secondary filters are updated.
     * @returns {void}
    */
    const handleSubmitFilters = (): void => {
        if(hasAnyDirtyFilters()) {
            setShowResetButton(true);
        }
        setResetFilters(false);

        if (selectedFloor) {
            handleContextFloorClick(selectedFloor);
        }

        setSecondarySingleFilter('rooms', filters.rooms);
        setSecondarySingleFilter('areaFrom', filters.areaFrom);
        setSecondarySingleFilter('areaTo', filters.areaTo);
    };

    /**
     * Resets all filters to their default states and hides the reset button.
     * Updates the secondary filters to default values.
     * @returns {void}
    */
    const handleResetFilters = (): void => {
        setShowResetButton(false);
        setResetFilters(true);

        setSecondarySingleFilter('rooms', []);
        setSecondarySingleFilter('areaFrom', null);
        setSecondarySingleFilter('areaTo', null);
    }

    /**
     * Checks if there are any active filters set.
     * @returns {boolean} - Returns true if any filter has been set, otherwise false.
    */
    const hasAnyDirtyFilters = (): boolean => (
        filters.rooms.length > 0 ||
        filters.areaFrom !== null ||
        filters.areaTo !== null
    );

    return (
        <div className={styles.wrapper}>
            <div className={styles.submitFilters}>
                <FloorFilter floors={floors} onSelectFloor={handleFloorSelect} />
                <FlatAreaFilter
                    reset={resetFilters}
                    onChange={(key, value) => handleAreaFilterChange(key, value)}
                />
                <FlatRoomFilter onSelectRoom={(rooms) => handleRoomSelect(rooms)} reset={resetFilters} />
                <button className={styles.submitButton} onClick={handleSubmitFilters}>Filtruj</button>
            </div>
            { (showResetButton && hasAnyDirtyFilters()) && <div className={styles.resetWrapper}>
                <div role="button" onClick={handleResetFilters}>
                    <img src={`${paths.assets}/icons/close.png`} alt="x" width={16} height={16} />
                    <span>Wyczyść filtry</span>
                </div>
            </div> }
            <FlatAvailableFilter />
        </div>
    );
};

export default FlatsFilters;